import type { ChatWithUserInfo } from '@/hooks/useArtistBookingDashboard'
import type { ChatStatus } from '@/types/models'
import type { Booking } from '@/types/booking'

// Import profile pictures so they get bundled correctly
import pic1 from '@/assets/profile_pics/078119643f8451b1d5593c60047f54c1.jpg'
import pic2 from '@/assets/profile_pics/14aa20a7bd9cec18abc42c449ad9531c.jpg'
import pic3 from '@/assets/profile_pics/1d7b1675e68da602dbbf26371ce24c83.jpg'
import pic4 from '@/assets/profile_pics/2a582a86295b7d9c603c48559cc9e15f.jpg'
import pic5 from '@/assets/profile_pics/329651bbe6f3d0155307f884be562753.jpg'
import pic6 from '@/assets/profile_pics/3e33c624d10160780af6a108f93bd57f.jpg'
import pic7 from '@/assets/profile_pics/440ab7c57587cbda3d290e97958eac62.jpg'
import pic8 from '@/assets/profile_pics/501299b4b08dd82af8ecaf1c39a49895.jpg'
import pic9 from '@/assets/profile_pics/5694a1fd9f309f128bc8230858a82fa5.jpg'
import pic10 from '@/assets/profile_pics/627ab7396b4b822ca8fe7ae54cef70b2.jpg'
import pic11 from '@/assets/profile_pics/7ae28e11e1a63c01071419ad831fb22b.jpg'
import pic12 from '@/assets/profile_pics/882bcd18ee4cb7c8440227022507455d.jpg'
import pic13 from '@/assets/profile_pics/88e20339c92c2e182f37ecedece3b1ec.jpg'
import pic14 from '@/assets/profile_pics/a1d550883da1ec73f59530902eb1f666.jpg'

/**
 * Demo chat configuration - simulates realistic user conversation flows
 * Format: { name, lastMessage, lastMessageFromArtist, hasBooking, status, daysAgo, bookingDaysFromNow }
 * 
 * Configured to test the sorting behavior in BookingChatList:
 * - "Booking Priority" sort: chats with bookings come first (sorted by scheduledTimestamp)
 * - "Recent" sort: sorted by lastMessageAt in descending order
 * - Status filters: artist_message_needed, waiting_for_client, active, booked, archived
 */
const DEMO_CHAT_CONFIG: Array<{
  name: string
  lastMessage: string
  lastMessageFromArtist: boolean
  hasBooking: boolean
  status: ChatStatus
  daysAgo: number // When was the last message
  bookingDaysFromNow?: number // When is the booking scheduled (only if hasBooking is true)
}> = [
  // HIGH PRIORITY - "Booking Priority" sort should show these first
  // Upcoming bookings (within 7 days, sorted by date)
  { name: 'MikeyYoo', lastMessage: 'See you tomorrow!', lastMessageFromArtist: false, hasBooking: true, status: 'booked', daysAgo: 0.5, bookingDaysFromNow: 1 },
  { name: 'smokeshou', lastMessage: 'Excited for Friday!', lastMessageFromArtist: true, hasBooking: true, status: 'booked', daysAgo: 2, bookingDaysFromNow: 3 },
  { name: 'RavenSkye7', lastMessage: 'Booked for next week', lastMessageFromArtist: false, hasBooking: true, status: 'booked', daysAgo: 1, bookingDaysFromNow: 7 },
  { name: 'foxxydesign', lastMessage: 'Perfect! I have an opening then', lastMessageFromArtist: true, hasBooking: true, status: 'booked', daysAgo: 0.25, bookingDaysFromNow: 10 },
  
  // MEDIUM PRIORITY - Need artist response (unread messages from client)
  { name: 'BearBone23', lastMessage: 'When are you available this month?', lastMessageFromArtist: false, hasBooking: false, status: 'artist_message_needed', daysAgo: 0.1, bookingDaysFromNow: undefined },
  { name: 'wolfpack_ink', lastMessage: 'Can you fit me in next week?', lastMessageFromArtist: false, hasBooking: false, status: 'artist_message_needed', daysAgo: 1.5, bookingDaysFromNow: undefined },
  { name: 'DeerVibe88', lastMessage: 'What about this design?', lastMessageFromArtist: false, hasBooking: false, status: 'artist_message_needed', daysAgo: 3, bookingDaysFromNow: undefined },
  
  // MEDIUM-LOW - Ongoing conversations (artist sent message, client hasn't responded yet)
  { name: 'EagleeEyes', lastMessage: 'Let me know if this works for you!', lastMessageFromArtist: true, hasBooking: false, status: 'active', daysAgo: 0.5, bookingDaysFromNow: undefined },
  { name: 'SnakeScale_J', lastMessage: 'I sketched up some ideas', lastMessageFromArtist: true, hasBooking: false, status: 'active', daysAgo: 2, bookingDaysFromNow: undefined },
  { name: 'lynxclaw5', lastMessage: 'Check out the design I made', lastMessageFromArtist: true, hasBooking: false, status: 'active', daysAgo: 4, bookingDaysFromNow: undefined },
  
  // LESS ACTIVE - Older conversations still in progress
  { name: 'tigerstripe', lastMessage: 'Thanks for the sketch!', lastMessageFromArtist: false, hasBooking: false, status: 'active', daysAgo: 7, bookingDaysFromNow: undefined },
  { name: 'HawkzArt', lastMessage: 'Let me think about it and get back', lastMessageFromArtist: false, hasBooking: false, status: 'active', daysAgo: 10, bookingDaysFromNow: undefined },
  { name: 'OwlMoon', lastMessage: 'I\'ll contact you soon!', lastMessageFromArtist: false, hasBooking: false, status: 'active', daysAgo: 14, bookingDaysFromNow: undefined },
  { name: 'cobraKID', lastMessage: 'Thanks for chatting!', lastMessageFromArtist: false, hasBooking: false, status: 'archived', daysAgo: 30, bookingDaysFromNow: undefined },
]

export const DEMO_USERS = [
  {
    uid: 'demo_user_1',
    displayName: 'MikeyYoo',
    instagram: '@MikeyYoo',
    email: 'mikey@example.com',
    avatarUrl: pic1,
  },
  {
    uid: 'demo_user_2',
    displayName: 'smokeshou',
    instagram: '@smokeshou',
    email: 'smoke@example.com',
    avatarUrl: pic2,
  },
  {
    uid: 'demo_user_3',
    displayName: 'RavenSkye7',
    instagram: '@RavenSkye7',
    email: 'ravensky@example.com',
    avatarUrl: pic3,
  },
  {
    uid: 'demo_user_4',
    displayName: 'foxxydesign',
    instagram: '@foxxydesign',
    email: 'foxx@example.com',
    avatarUrl: pic4,
  },
  {
    uid: 'demo_user_5',
    displayName: 'BearBone23',
    instagram: '@BearBone23',
    email: 'bearbone@example.com',
    avatarUrl: pic5,
  },
  {
    uid: 'demo_user_6',
    displayName: 'wolfpack_ink',
    instagram: '@wolfpack_ink',
    email: 'wolfpack@example.com',
    avatarUrl: pic6,
  },
  {
    uid: 'demo_user_7',
    displayName: 'EagleeEyes',
    instagram: '@EagleeEyes',
    email: 'eagleeyes@example.com',
    avatarUrl: pic7,
  },
  {
    uid: 'demo_user_8',
    displayName: 'DeerVibe88',
    instagram: '@DeerVibe88',
    email: 'deervibe@example.com',
    avatarUrl: pic8,
  },
  {
    uid: 'demo_user_9',
    displayName: 'tigerstripe',
    instagram: '@tigerstripe',
    email: 'tiger@example.com',
    avatarUrl: pic9,
  },
  {
    uid: 'demo_user_10',
    displayName: 'SnakeScale_J',
    instagram: '@SnakeScale_J',
    email: 'snakej@example.com',
    avatarUrl: pic10,
  },
  {
    uid: 'demo_user_11',
    displayName: 'HawkzArt',
    instagram: '@HawkzArt',
    email: 'hawkz@example.com',
    avatarUrl: pic11,
  },
  {
    uid: 'demo_user_12',
    displayName: 'lynxclaw5',
    instagram: '@lynxclaw5',
    email: 'lynx@example.com',
    avatarUrl: pic12,
  },
  {
    uid: 'demo_user_13',
    displayName: 'OwlMoon',
    instagram: '@OwlMoon',
    email: 'owlmoon@example.com',
    avatarUrl: pic13,
  },
  {
    uid: 'demo_user_14',
    displayName: 'cobraKID',
    instagram: '@cobraKID',
    email: 'cobra@example.com',
    avatarUrl: pic14,
  },
]

const DEMO_MESSAGES = [
  "Hi! I'm interested in a custom design",
  'Love your work! Can you help with something?',
  'Do you have any availability coming up?',
  "I've been following your page, really dig your style",
  'Would love to talk about a piece',
  'Your portfolio is incredible!',
]

/**
 * DEMO DATA BEHAVIOR DOCUMENTATION
 * ==================================
 * 
 * This demo data simulates realistic user interactions to test sorting/filtering:
 * 
 * BOOKING PRIORITY SORT behavior:
 * - Chats with bookings are sorted by scheduledTimestamp (earliest first)
 * - Chats without bookings are sorted by status priority, then by recency
 * - Expected order: upcoming bookings → urgent messages → waiting for response → old conversations
 * 
 * RECENT SORT behavior:
 * - All chats sorted by lastMessageAt in descending order (newest first)
 * - Expected order: very recent messages at top → older messages at bottom
 * 
 * STATUS FILTERING behavior:
 * - artist_message_needed: Client sent message, artist hasn't responded (unread)
 * - waiting_for_client: Artist sent message, awaiting client response
 * - booked: Chat has a confirmed booking with scheduledTimestamp
 * - active: Ongoing conversations without urgent action
 * - archived: Old conversations no longer active
 * 
 * REALISTIC PATTERNS IN TEST DATA:
 * 1. Bookings have scheduledTimestamp set to future dates (1-10 days from now)
 * 2. Recent messages (0-1 days ago) have higher priority and are often unread
 * 3. Booking Priority sort mirrors real artist workflow: handle bookings first, then urgent messages
 * 4. Messages preserve read/unread status: client messages are unread (need response), artist messages are read
 * 5. Conversation history shows typical flow: client initiation → artist response → ongoing dialogue
 */

/**
 * Generate demo booking data with realistic scheduling
 * These bookings simulate how real users schedule their tattoo appointments
 */
function generateDemoBooking(artistId: string, user: any, config: any, index: number, now: Date): Booking | undefined {
  if (!config.hasBooking || config.bookingDaysFromNow === undefined) {
    return undefined
  }

  const scheduledTimestamp = new Date(now.getTime() + config.bookingDaysFromNow * 24 * 60 * 60 * 1000)
  // Set to a reasonable time (e.g., 2 PM)
  scheduledTimestamp.setHours(14, 0, 0, 0)

  return {
    id: `demo_booking_${index + 1}`,
    chatId: `demo_chat_${index + 1}`,
    userId: user.uid,
    artistId: artistId,
    clientUsername: config.name,
    type: index % 2 === 0 ? 'flash' : 'custom',
    shortSummary: `Tattoo booking with ${config.name}`,
    scheduledTimestamp: scheduledTimestamp,
    createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000), // Created 5 days ago
    estimatedTime: 60 + (index % 3) * 60, // 60, 120, or 180 minutes
  }
}

export function generateDemoChats(artistId: string): ChatWithUserInfo[] {
  // Set constant base times
  const baseDate = new Date()
  baseDate.setHours(0, 0, 0, 0) // Set to start of today
  
  // Define constant message times
  const messageTimes = {
    today9am: new Date(baseDate.getTime() + 9 * 60 * 60 * 1000),
    yesterday9am: new Date(baseDate.getTime() - 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
    threeDaysAgo9am: new Date(baseDate.getTime() - 3 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
    sevenDaysAgo9am: new Date(baseDate.getTime() - 7 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
    tenDaysAgo9am: new Date(baseDate.getTime() - 10 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
    fourteenDaysAgo9am: new Date(baseDate.getTime() - 14 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
    thirtyDaysAgo9am: new Date(baseDate.getTime() - 30 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000),
  }

  return DEMO_USERS.map((user, index) => {
    const config = DEMO_CHAT_CONFIG[index]
    
    // Assign constant message time based on daysAgo
    let lastMessageAt = messageTimes.today9am
    if (config.daysAgo >= 30) {
      lastMessageAt = messageTimes.thirtyDaysAgo9am
    } else if (config.daysAgo >= 14) {
      lastMessageAt = messageTimes.fourteenDaysAgo9am
    } else if (config.daysAgo >= 10) {
      lastMessageAt = messageTimes.tenDaysAgo9am
    } else if (config.daysAgo >= 7) {
      lastMessageAt = messageTimes.sevenDaysAgo9am
    } else if (config.daysAgo >= 3) {
      lastMessageAt = messageTimes.threeDaysAgo9am
    } else if (config.daysAgo >= 1) {
      lastMessageAt = messageTimes.yesterday9am
    } else {
      lastMessageAt = messageTimes.today9am
    }
    
    // Determine if last message is from user or artist based on config
    const isLastMessageFromUser = !config.lastMessageFromArtist
    const lastSenderId = isLastMessageFromUser ? user.uid : 'artist'
    const lastSenderType = isLastMessageFromUser ? 'user' : 'artist'
    
    // Messages from clients are unread, messages from artist are read
    const isLastMessageUnread = isLastMessageFromUser
    
    // Create conversation history - first message 3 days before last message
    const firstMessageAt = new Date(lastMessageAt.getTime() - 3 * 24 * 60 * 60 * 1000)

    // Generate booking if applicable
    const booking = config.hasBooking ? generateDemoBooking(artistId, user, config, index, baseDate) : undefined

    return {
      id: `demo_chat_${index + 1}`,
      userId: user.uid,
      artistId: artistId,
      flashId: null,
      createdAt: firstMessageAt,
      lastMessageAt: lastMessageAt,
      lastMessage: config.lastMessage,
      status: config.status,
      bookingId: booking?.id,
      booking: booking,
      hasBooking: config.hasBooking,
      messages: [
        {
          id: `msg_${index}_1`,
          content: DEMO_MESSAGES[index % DEMO_MESSAGES.length],
          senderId: user.uid,
          senderType: 'user',
          timestamp: firstMessageAt,
          type: 'text',
          isRead: true,
        },
        {
          id: `msg_${index}_2`,
          content: "I can help with that! Let's chat about it.",
          senderId: 'artist',
          senderType: 'artist',
          timestamp: new Date(firstMessageAt.getTime() + 2 * 60 * 60 * 1000), // 2 hours later
          type: 'text',
          isRead: true,
        },
        {
          id: `msg_${index}_3`,
          content: config.lastMessage,
          senderId: lastSenderId,
          senderType: lastSenderType,
          timestamp: lastMessageAt,
          type: 'text',
          isRead: !isLastMessageUnread,
        },
      ],
      userInfo: user,
    }
  })
}

